import { addDays, format } from 'date-fns';

// `cn` lives in `@clockwise/ui` so the app and the shared widgets resolve
// conflicting Tailwind utilities through ONE `tailwind-merge`. Re-exported here
// because `@/lib/utils` is the import path the whole app already uses.
export { cn } from '@clockwise/ui';

// Today as YYYY-MM-DD in the **user's local timezone**.
// `new Date().toISOString().slice(0, 10)` is UTC — wrong on the user side of
// midnight UTC. Date-only fields (effectiveFrom/To, timesheet date, …) are
// "the day the user is on" and must use local time.
export function localTodayIso(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

// Tomorrow as YYYY-MM-DD in the user's local timezone. `effectiveTo` is an
// **exclusive** end boundary — membership is active while `effectiveTo > date`
// (see customer_team repository). So to keep someone active through the end of
// today, the end date must be tomorrow, not today. Ending with `localTodayIso`
// would revoke access for the whole current day.
export function localTomorrowIso(): string {
  return format(addDays(new Date(), 1), 'yyyy-MM-dd');
}

// Format a date-only ISO string (`YYYY-MM-DD`) for display, e.g. "Jun 7, 2026".
// Parsed as UTC midnight so a plain calendar date never shifts a day under the
// viewer's local timezone (the classic `new Date('2026-06-07')` off-by-one).
// Use this for every date-only column so the format stays uniform across the
// app instead of being re-derived per component.
const INSTANT_DATE_FORMAT = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' });

/** The calendar date of an instant (`sentAt`, `approvedAt`), in the viewer's
 *  own timezone — unlike `formatIsoDate`, which is for date-only strings. */
export function formatInstantDate(iso: string): string {
  return INSTANT_DATE_FORMAT.format(new Date(iso));
}

export function formatIsoDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}
